import { cva } from 'class-variance-authority';

import { GmLevelCoin } from '@/components/entities/gamification/GmLevelCoin';
import { GmProgressBar } from '@/components/entities/gamification/GmProgressBar';
import { SkeletonChild, SkeletonParent } from '@/components/ui/Skeleton/skeleton';

import { cn } from '@/utils/cn';
import { LinkToMissionPageWrapper } from './components/LinkToMissionPageWrapper';

const gmLevelProgressVariants = cva('flex min-w-0 flex-col', {
  variants: {
    appearance: {
      mobileBar: '',
      statusTable: '',
    },
  },
  defaultVariants: {
    appearance: 'mobileBar',
  },
});

const gmLevelProgressMetaVariants = cva('flex justify-between text-xs font-medium leading-4 gm-text-golden-1', {
  variants: {
    appearance: {
      mobileBar: 'mt-[0.313rem]',
      statusTable: 'mt-1',
    },
  },
  defaultVariants: {
    appearance: 'mobileBar',
  },
});

const gmLevelProgressLabelVariants = cva('', {
  variants: {
    appearance: {
      mobileBar: 'ml-1.25',
      statusTable: 'ml-2',
    },
  },
  defaultVariants: {
    appearance: 'mobileBar',
  },
});

const gmLevelProgressBarOffsetVariants = cva('absolute right-0 top-1/2 -translate-y-1/2', {
  variants: {
    appearance: {
      mobileBar: 'left-5.75',
      statusTable: 'left-4 rounded-full',
    },
  },
  defaultVariants: {
    appearance: 'mobileBar',
  },
});

const gmLevelProgressConfig = {
  mobileBar: {
    coinVariant: 'mobileBar',
    progressAppearance: 'mobileBar',
    unit: 'XP',
  },
  statusTable: {
    coinVariant: 'mobileBar',
    progressAppearance: 'mobileBar',
    unit: 'LVL',
  },
} as const;

type GmLevelProgressAppearance = keyof typeof gmLevelProgressConfig;

interface GmLevelProgressProps {
  appearance?: GmLevelProgressAppearance;
  className?: string;
  currentValue: number;
  levelNumber: number;
  progressPercent: number;
  targetValue: number;
  loading?: boolean;
}

export const GmLevelProgress = ({
  appearance = 'mobileBar',
  className,
  currentValue,
  levelNumber,
  loading,
  progressPercent,
  targetValue,
}: GmLevelProgressProps) => {
  const config = gmLevelProgressConfig[appearance];
  const shouldUseLinkForMobileBar = appearance === 'mobileBar';

  if (loading) {
    return (
      <div className="my-auto w-full">
        <SkeletonParent className="h-3.5 rounded-lg w-full">
          <SkeletonChild />
        </SkeletonParent>
      </div>
    );
  }

  return (
    <div className={cn(gmLevelProgressVariants({ appearance }), className)}>
      <LinkToMissionPageWrapper enabled={shouldUseLinkForMobileBar}>
        <div className="relative w-full">
          <GmLevelCoin levelNumber={levelNumber} className="relative z-1" appearance={config.coinVariant} />
          <div className={cn(gmLevelProgressBarOffsetVariants({ appearance }), 'z-0')}>
            <GmProgressBar
              showLightEffect={false}
              percent={progressPercent}
              appearance={config.progressAppearance}
              className="**:data-[slot=progressBarFill]:min-w-2 **:data-[slot=progressBarTrack]:min-w-1"
            />
          </div>
        </div>
      </LinkToMissionPageWrapper>
      <div className={gmLevelProgressMetaVariants({ appearance })}>
        <span className={gmLevelProgressLabelVariants({ appearance })}>
          LVL
        </span>

        <LinkToMissionPageWrapper enabled={shouldUseLinkForMobileBar}>
          <span className="transition-colors duration-300 hover:text-white hover:[-webkit-text-fill-color:white]">
            {currentValue}
            /
            {targetValue}
            {' '}
            {config.unit}
          </span>
        </LinkToMissionPageWrapper>

      </div>
    </div>
  );
};
